import React, { useState } from 'react';
import { createRecipe } from '../api';
import { useNavigate } from 'react-router-dom';
import { openDatabase } from '../utils/database';
import { Box, Typography, TextField, Button, Grid, InputAdornment } from '@mui/material';

const CreateRecipePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    receipt: '',
    ingredients: '',
    servings: '',
    preparationTime: '',
    cookingTime: '',
    image: null,
  });
  const [imagePreview, setImagePreview] = useState(null); // État pour la prévisualisation de l'image
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, image: file });
    setImagePreview(URL.createObjectURL(file)); // Générer une URL pour la prévisualisation
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!formData.name || !formData.description || !formData.receipt || !formData.ingredients || !formData.servings || !formData.preparationTime || !formData.cookingTime) {
      alert('Tous les champs obligatoires doivent être remplis.');
      return;
    }
  
    const recipe = {
      name: formData.name,
      description: formData.description,
      receipt: formData.receipt.split(',').map((step) => step.trim()),
      ingredients: formData.ingredients.split(',').map((item) => {
        const [name, quantity] = item.split('-').map((str) => str.trim());
        return { name, quantity };
      }),
      servings: formData.servings,
      preparationTime: formData.preparationTime,
      cookingTime: formData.cookingTime,
      image: formData.image ? formData.image : null, // Stockez uniquement le nom du fichier
    };
  
    if (navigator.onLine) {
      try {
        const formDataToSend = new FormData();
        formDataToSend.append('name', recipe.name);
        formDataToSend.append('description', recipe.description);
        formDataToSend.append('receipt', JSON.stringify(recipe.receipt));
        formDataToSend.append('ingredients', JSON.stringify(recipe.ingredients));
        formDataToSend.append('servings', recipe.servings);
        formDataToSend.append('preparationTime', recipe.preparationTime);
        formDataToSend.append('cookingTime', recipe.cookingTime);
        if (formData.image) {
          formDataToSend.append('image', formData.image);
        }
  
        await createRecipe(formDataToSend);
        alert('Recette créée avec succès !');
        navigate('/');
      } catch (err) {
        setError(err.message);
      }
    } else {
      try {
        const db = await openDatabase();
        const transaction = db.transaction('offline-recipes', 'readwrite');
        const store = transaction.objectStore('offline-recipes');
        store.add(recipe); // Stockez l'objet JSON sérialisable
  
        alert('Vous êtes hors ligne. La recette sera synchronisée lorsque vous serez en ligne.');
      } catch (error) {
        console.error('Erreur lors de l\'enregistrement hors ligne :', error);
        alert('Erreur lors de l\'enregistrement hors ligne.');
      }
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 3,
        maxWidth: 600,
        margin: '0 auto',
        boxShadow: 3,
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="h4" gutterBottom>
        Créer une Recette
      </Typography>

      {error && (
        <Typography variant="body2" sx={{ color: 'error.main', marginBottom: 2 }}>
          {error}
        </Typography>
      )}

      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <TextField
          label="Nom de la recette"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          fullWidth
          margin="normal"
          variant="outlined"
        />

        <TextField
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          required
          fullWidth
          multiline
          rows={4}
          margin="normal"
          variant="outlined"
        />

        <TextField
          label="Instructions (séparées par des virgules)"
          name="receipt"
          value={formData.receipt}
          onChange={handleChange}
          required
          fullWidth
          multiline
          rows={4}
          margin="normal"
          variant="outlined"
        />

        <TextField
          label="Ingrédients (format : 'Tomate - 2, Oignon - 1')"
          name="ingredients"
          value={formData.ingredients}
          onChange={handleChange}
          required
          fullWidth
          multiline
          rows={4}
          margin="normal"
          variant="outlined"
        />

          <Grid item xs={12} md={6}>
            <TextField
              label="Nombre de personnes"
              name="servings"
              type="number"
              value={formData.servings}
              onChange={handleChange}
              required
              fullWidth
              margin="normal"
              variant="outlined"
              InputProps={{
                startAdornment: <InputAdornment position="start">pers.</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Temps de préparation (min)"
              name="preparationTime"
              type="number"
              value={formData.preparationTime}
              onChange={handleChange}
              required
              fullWidth
              margin="normal"
              variant="outlined"
              InputProps={{
                startAdornment: <InputAdornment position="start">min</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Temps de cuisson (min)"
              name="cookingTime"
              type="number"
              value={formData.cookingTime}
              onChange={handleChange}
              required
              fullWidth
              margin="normal"
              variant="outlined"
              InputProps={{
                startAdornment: <InputAdornment position="start">min</InputAdornment>,
              }}
            />
          </Grid>

        <Button
          variant="outlined"
          component="label"
          fullWidth
          sx={{ mt: 2 }}
        >
          Ajouter une image
          <input
            type="file"
            name="image"
            accept="image/*"
            onChange={handleFileChange}
            hidden
          />
        </Button>

        {imagePreview && (
          <Box sx={{ mt: 2 }}>
            <img src={imagePreview} alt="Prévisualisation" style={{ maxWidth: '100%' }} />
          </Box>
        )}

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 3 }}
        >
          Créer
        </Button>
      </form>
    </Box>
  );
};

export default CreateRecipePage;